import type { Space } from '@cocoonspace/sdk-js/types/space'
import { useMediaQuery } from '@cocoonspace/shared/hooks/use-media-query.hook'
import { AnimatePresence } from 'framer-motion'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useState } from 'react'
import { toast } from 'sonner'
import { ResizablePanel } from '~/components/molecules/resizable-panel'
import { Infobox } from '~/domains/booking/components/molecules/infobox'
import {
	BookingStepper,
	type BookingSteps,
	type BookingType,
	type TimeSlot,
} from '~/domains/booking/components/templates/booking-stepper'
import { useBookingFindOrCreateMutation } from '~/domains/booking/hooks/use-booking-find-or-create.hook'
import type { Direction } from '~/domains/booking/hooks/use-stepper.hook'
import { cn } from '~/lib/utils'
import { useTracking } from '~/providers/tracking-provider'
import { twTheme } from '~/theme'

export const SpaceDetailsSidebar = ({
	space,
	showInfoCard,
}: {
	space: Space
	showInfoCard?: boolean
}) => {
	const router = useRouter()
	const { t } = useTranslation()
	const isMobile = useMediaQuery(`(max-width: ${twTheme.theme.screens.md})`)
	const isTablet = useMediaQuery(`(min-width: ${twTheme.theme.screens.md})`)
	const { track } = useTracking()

	const isInfoCardVisible = !isMobile || (isMobile && showInfoCard)
	const [step, setStep] = useState<BookingSteps>()
	const [type, setType] = useState<BookingType>('meeting')
	const [activeTab, setActiveTab] = useState('daily')

	const [hasBookingOption, setHasBookingOption] = useState(false)

	useEffect(() => {
		if (router.isReady) {
			const { day, start, end } = router.query

			const tab =
				Array.isArray(day) || (!!day && !(start && end)) ? 'multidate' : 'daily'

			setActiveTab(tab)
		}
	}, [router.isReady])

	const scrollSidebarIntoView = () => {
		window.scrollTo({ top: 501, behavior: 'smooth' })
	}

	const handleOnChange = useCallback(
		({
			bookingOption,
			activeStep,
			direction,
		}: {
			activeStep: BookingSteps
			bookingOption: TimeSlot
			direction: Direction
		}) => {
			setHasBookingOption(
				!!bookingOption.day && !!bookingOption.start && !!bookingOption.end,
			)
			setStep(activeStep)

			if (!isMobile && activeStep === 'monthSelect' && direction === 'next') {
				scrollSidebarIntoView()
			}
		},
		[isMobile],
	)

	const bookingFindOrCreateMutation = useBookingFindOrCreateMutation()

	const handleOnSuccess = (
		bookingOption: TimeSlot,
		type: 'visit' | 'meeting',
	) => {
		bookingFindOrCreateMutation.mutate(
			{
				day: bookingOption.day!,
				start: bookingOption.start!.replace('-', ':'),
				end: bookingOption.end!.replace('-', ':'),
				type,
				space: space.id,
			},
			{
				onSuccess(data) {
					track.click(
						activeTab === 'multidate'
							? 'spaceDetails-booking-days-btn'
							: 'spaceDetails-booking-hours-btn',
					)

					router.push(`/booking/${data.booking_id}/payment`)
				},
				onError(error: any) {
					const errorCode = error?.response?.data?.errors?.[0].code as
						| string
						| undefined

					toast.error(
						t([
							`errors:context.booking.${errorCode}`,
							`errors:api.${errorCode}`,
							'errors:generic',
						]),
					)
				},
			},
		)
	}

	return (
		<div className='fixed inset-x-0 top-auto bottom-0 z-10 flex flex-col md:sticky md:inset-x-auto md:top-[108px] md:bottom-auto md:gap-4'>
			{/* TODO: remove the conditional 260px class when tw is ready */}
			<ResizablePanel
				className={cn(
					'relative order-2 touch-none overflow-hidden rounded-lg bg-white max-md:rounded-b-none max-md:border-b-0 md:order-1',
					{ 'min-h-[260px]': isTablet },
				)}
			>
				{router.isReady && type === 'meeting' && (
					<BookingStepper
						activeTab={activeTab}
						space={space}
						defaultValue={
							router.query.day && router.query.start && router.query.end
								? {
										day: router.query.day as string,
										start: (router.query.start as string).replace('-', ':'),
										end: (router.query.end as string).replace('-', ':'),
									}
								: {
										start: '08:00',
										end: '09:00',
									}
						}
						activeStep={
							router.query.day && router.query.start && router.query.end
								? 'timeSelect'
								: 'start'
						}
						type='meeting'
						onActiveTabChange={(tab) => {
							scrollSidebarIntoView()
							setActiveTab(tab)
						}}
						onChange={handleOnChange}
						onSuccess={({ bookingOption }) =>
							handleOnSuccess(bookingOption, 'meeting')
						}
					/>
				)}
			</ResizablePanel>

			<AnimatePresence>
				{isInfoCardVisible && type === 'meeting' && (
					<Infobox
						step={step}
						isMultidate={activeTab === 'multidate'}
						hasBookingOption={hasBookingOption}
						spaceCapacity={space.capacity}
					/>
				)}
			</AnimatePresence>
		</div>
	)
}
